import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  DOCUMENT,
  ChangeDetectorRef,
} from '@angular/core';

import { TUser } from 'src/app/project/modules/user/user.model';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { PreferencesService } from 'src/app/project/modules/preferences/preferences-service/preferences.service';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { UserService } from 'src/app/project/modules/user/user.service';
import { GoogleAnalyticsService } from 'src/app/project/services/analytics/google-analytics.service';
import { DropdownService } from '../../../../components/dropdown/dropdown-service/dropdown.service';
import { ActiveService } from '../../../../services/active/active.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  SiteTableColumnsDataService,
  TColumnsData,
} from '../../site-table/site-table-columns-data.service';

import { checkCustomWorkspaceId } from 'src/app/project/modules/workspace/workspace';

import { catchError, tap } from 'rxjs';
import { getDragIcon } from 'src/app/core/helpers/dragging/get-drag-icon';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { EBasicField, EBasicFieldShort } from 'src/app/project/shared/enums/basic-fields-enums';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { ResponseErrorService } from '../../../errors/response-error.service';
import { TSavedView } from '../../../saved-views/models/saved-view.model';
import { SavedViewsService } from '../../../saved-views/saved-views.service';
import { ResetTableService } from '../../reset-table.service';
import { TColumn } from '../../site-table/columns/column.model';
import { TableColumnsService } from '../../site-table/columns/table-columns.service';
import { CustomTableService } from '../../site-table/custom-table/custom-table.service';
import Table from '../../site-table/custom-table/table/Table';
import { checkFieldVolyTooltip } from '../utils/check-field-voly-tooltip';
import { getFieldIconFromFile } from '../utils/get-field-icon';

type TColumnRow = {
  column: TColumn;
  label: string;
  icon: string;
  iconTooltip: string;
};

@Component({
  selector: 'app-site-options-column-dropdown',
  templateUrl: './site-options-column-dropdown.component.html',
  styleUrls: ['./site-options-column-dropdown.component.scss'],
  standalone: false,
})
export class SiteOptionsColumnDropdownComponent implements OnInit {
  @ViewChild('columnTable') columnsTableElement: ElementRef;

  columns: TColumnsData = this.siteTableColumnsDataService.getColumns();

  public tagsVisible: boolean;
  public user: TUser;
  workspace: TWorkspace;
  workspaces: TWorkspacesById;
  siteId = '';
  draggedColumn: TColumn = null;
  destinationIndex: number = null;
  sortedColumns: TColumn[];
  filteredColumns: TColumnRow[];
  searchQuery = '';
  searchBarVisible = false;
  rowHeight = 18;
  rowGap = 8;
  linePosition = this.rowHeight + this.rowGap;
  scrollPosition = 0;
  scrolledToBottom = false;
  saveViewProcessing = false;

  isDropdownVisible: boolean;
  activeViewName = '';
  activeView: TSavedView;

  EIconPath = EIconPath;

  private table: Table = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private dropdownService: DropdownService,
    private siteTableColumnsDataService: SiteTableColumnsDataService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private preferencesService: PreferencesService,
    private customTableService: CustomTableService,
    private savedViewsService: SavedViewsService,
    private responseErrorService: ResponseErrorService,
    private tableColumnsService: TableColumnsService,
    private resetTableService: ResetTableService,
    private featureFlagService: FeatureFlagService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const columns = this.tableColumnsService.getColumns();
    this.table = this.customTableService.getTable();

    this.sortedColumns = columns;
    this.applyFilter();

    this.searchBarVisible = this.featureFlagService.getFlag(EFeatureFlag.TABLE_COLUMNS_SEARCH_BAR);

    this.workspace = this.workspaceService.getActiveWorkspace();
    this.workspaces = this.workspaceService.getWorkspaces();
    const activeViewId = this.savedViewsService.getSelectedViewId(this.workspace?.workspaceId);
    this.activeViewName = this.savedViewsService.getView(activeViewId)?.name;
    this.user = this.userService.getUser();

    if (this.workspace) {
      const tagPermissions = this.permissionsService.getTagPermissions(this.workspace?.workspaceId);

      this.siteId = checkCustomWorkspaceId() || this.workspace?.workspaceId;
      this.tagsVisible = tagPermissions.read;
    } else {
      this.workspaces = this.workspaceService.getWorkspaces();
      this.columns = this.siteTableColumnsDataService.getAllColumns();
      this.tagsVisible = true;
      this.siteId = checkCustomWorkspaceId();
    }

    this.activeView = this.savedViewsService.getView(activeViewId);
    this.activeViewName = this.activeView?.name;
  }

  get isFiltered(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.applyFilter();
  }

  private applyFilter(): void {
    const rows = this.sortedColumns.map((column) => ({
      column,
      label: this.getColumnLabel(column),
      icon: getFieldIconFromFile(column),
      iconTooltip: checkFieldVolyTooltip(column),
    }));
    const query = this.searchQuery.trim().toLowerCase();

    if (!query) {
      this.filteredColumns = rows;

      return;
    }

    this.filteredColumns = rows.filter((row) => row.label.toLowerCase().includes(query));
  }

  private getColumnLabel(column: TColumn): string {
    switch (column.name) {
      case EBasicFieldShort.PRIORITY:
        return EBasicField.PRIORITY;
      case EBasicFieldShort.STATUS:
        return EBasicField.STATUS;
      default:
        return column.name;
    }
  }

  toggleColumn(_column: TColumn): void {
    const columnType = this.googleAnalyticsService.getColumnType(_column);
    const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();

    if (_column.hidden) {
      logEventInGTAG(EGoogleEventSite.SITE__COLUMNS__SHOW, {
        source: EGoogleEventCategory.SITE,
        type: columnType,
      });
    } else {
      logEventInGTAG(EGoogleEventSite.SITE__COLUMNS__HIDE, {
        source: EGoogleEventCategory.SITE,
        type: columnType,
      });
    }

    this.customTableService.hasUpdatedColumns();

    this.table.toggleColumn(_column);
    this.preferencesService.savePreferences(workspaceId);
  }

  resetColumns(): void {
    this.resetTableService.resetColumns({ workspaceId: this.workspace?.workspaceId });

    this.hideDropdown();
  }

  saveColumnsToView(): void {
    const workspaceId = this.workspace ? this.workspace.workspaceId : checkCustomWorkspaceId();
    const columns = this.tableColumnsService.getColumns();

    this.saveViewProcessing = true;

    const viewToSave = {
      ...this.activeView,
      columns: columns,
    };

    this.savedViewsService
      .saveToView(viewToSave, workspaceId)
      .pipe(
        tap((updatedView) => {
          this.activeView = updatedView;

          this.saveViewProcessing = false;
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.saveViewProcessing = false;
          this.cdr.markForCheck();
          return this.responseErrorService.handleRequestError(
            error,
            'Failed to save columns to view',
          );
        }),
      )
      .subscribe();
  }

  onDragStart(event: DragEvent, i: number, column: TColumn): void {
    const img = new Image();

    this.draggedColumn = column;
    this.draggedColumn.index = i;
    img.src = getDragIcon();
    this.linePosition =
      this.columnsTableElement.nativeElement.children[i].offsetTop -
      this.rowHeight -
      this.rowGap +
      this.rowHeight / 2;
    event.dataTransfer.setData('text/plain', column.name);
    event.dataTransfer.setDragImage(img, 0, 0);
  }

  onDragOver(event: DragEvent, index: number, field: TColumn): void {
    event.preventDefault();
    event.stopPropagation();

    const itemRowElement = this.document.getElementById('itemRow-' + field.name);
    const targetElementCoords = itemRowElement.getBoundingClientRect();
    const elementCenterY =
      targetElementCoords.top + (targetElementCoords.bottom - targetElementCoords.top) / 2;

    let offset = 0;

    this.destinationIndex = index;
    let linePositionIndex = this.destinationIndex;

    if (event.clientY > elementCenterY) {
      this.destinationIndex += 1;

      linePositionIndex = this.destinationIndex;

      if (index >= this.draggedColumn.index) {
        this.destinationIndex -= 1;
      }
    } else {
      if (index > this.draggedColumn.index) {
        this.destinationIndex -= 1;
      }
    }

    if (linePositionIndex > this.columnsTableElement.nativeElement.children.length - 1) {
      linePositionIndex = this.columnsTableElement.nativeElement.children.length - 1;

      offset = this.rowHeight + this.rowGap;
    }

    if (linePositionIndex === 0) {
      this.linePosition = -this.rowGap / 2;
    } else {
      this.linePosition =
        this.columnsTableElement.nativeElement.children[linePositionIndex].offsetTop -
        this.rowHeight +
        this.rowGap * 1.5 +
        offset -
        this.scrollPosition;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  updateColumnValue(column: TColumn): void {
    this.toggleColumn(column);
  }

  onDragEnd(): void {
    const columns = this.tableColumnsService.getColumns();

    if (columns) {
      const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();

      if (this.draggedColumn.index !== this.destinationIndex) {
        const column = columns.find((_column) => _column.name === this.draggedColumn.name);

        if (column) {
          const columnType = this.googleAnalyticsService.getColumnType(column);

          logEventInGTAG(EGoogleEventSite.SITE__COLUMNS__MOVE, {
            source: EGoogleEventCategory.SITE,
            type: columnType,
          });
        }

        this.table.moveColumn(this.draggedColumn.index, this.destinationIndex);

        this.sortedColumns = columns;
        this.applyFilter();
      }

      this.draggedColumn = null;
      this.destinationIndex = null;

      this.preferencesService.savePreferences(workspaceId);
    }
  }

  scrollHandler(event: Event): void {
    if (event.target instanceof HTMLElement) {
      this.scrollPosition = event.target.scrollTop;
      this.scrolledToBottom =
        event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
    }
  }

  hideDropdown(): void {
    this.dropdownService.hideDropdown();
  }
}
